import { getT } from '@/i18n';
import { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppLinkActions } from './components/AppLinkActions';
import { PlayAnalyticsConsent } from './components/PlayAnalyticsConsent';
import { ScrollManager } from '@/components/ScrollManager';
import { AppLockGate } from '@/components/AppLockGate';
import { needsUnlock } from '@/lib/appLock';
import { BadgeSync } from '@/components/BadgeSync';
import { CommunityLockPrompt } from '@/components/CommunityLockPrompt';
import { AssistantChat } from '@/components/AssistantChat';
import { useAssistantStore } from '@/store/assistantStore';
import { useOnboardingStore } from '@/store/onboardingStore';
import { useOnboarding } from '@/hooks/useOnboarding';
import { TutorialLayer } from '@/components/Tutorial';
import { ThemeProvider } from './hooks/ThemeProvider';
import { GAME_LOOP_BUILD } from './lib/gameLoopBuild';
import { AuthProvider, useAuth } from './hooks/AuthProvider';
import { ProfileProvider, useProfile } from './hooks/ProfileProvider';
import { RecorderProvider, useRecorderUploadStatus } from './hooks/RecorderProvider';
import { RivalProvider } from './hooks/RivalProvider';
import { NotificationsProvider } from './hooks/useNotifications';
import { InboxProvider } from './hooks/useInbox';
import { Dock } from './components/Dock';
import { ResumeActivityPrompt } from './components/ResumeActivityPrompt';
import { PassportStampHost } from './components/passport/PassportStampHost';
import { Button } from './components/ui';
import { HomeScreen } from './screens/HomeScreen';
import { api, ApiError, type AccountDeletionState } from './lib/api';
import { formatDateTime } from './lib/format';
import { addNativePushActionListener } from './lib/push';
import { debugModeAvailable } from './lib/debugMode';
import { useAndroidBack } from '@/hooks/useAndroidBack';
import { startAppUsageTracking } from './lib/appUsage';
import { useT } from './i18n/react';
import { setLocalePreviewEnabled } from './i18n';
import { isLocalePreviewAvailable } from './i18n/rollout';

/**
 * MINDEN KÉPERNYŐ LUSTÁN TÖLTŐDIK, A HOME KIVÉTELÉVEL.
 *
 * ⚠️ GRUNDO #21 energiaelemzés, D2: korábban mind a 22 játékos képernyő
 * statikus import volt — a `Kezdőlap → Beállítások → Adatvédelem` útvonal
 * ugyanazt a JS-darabot töltötte be, mint a `Kezdőlap → Rögzítés`, pedig a
 * kettőnek semmi köze egymáshoz. A Home marad statikus, mert ez a
 * belépés utáni ALAPÉRTELMEZETT nézet — natív appban a JS-fájl helyben
 * van, tehát ez nem hálózati kérdés, hanem FORDÍTÁSI/VÉGREHAJTÁSI idő
 * minden hidegindításnál, amit nem érdemes a ritkábban látogatott
 * képernyőkért fizetni.
 *
 * Az admin terület már korábban is lusta volt (`docs/06` döntése) — az
 * indoklás ugyanaz, csak most minden JÁTÉKOS képernyőre is kiterjed.
 */
const LiveBattleScreen = lazy(() => import('./screens/LiveBattleScreen').then((m) => ({ default: m.LiveBattleScreen })));
const LiveViewerScreen = lazy(() => import('./screens/LiveViewerScreen').then((m) => ({ default: m.LiveViewerScreen })));
const TerritoryScreen = lazy(() => import('./screens/TerritoryScreen').then((m) => ({ default: m.TerritoryScreen })));
const TrackingScreen = lazy(() => import('./screens/TrackingScreen').then((m) => ({ default: m.TrackingScreen })));
const DiscoverScreen = lazy(() => import('./screens/DiscoverScreen').then((m) => ({ default: m.DiscoverScreen })));
const NewsScreen = lazy(() => import('./screens/NewsScreen').then((m) => ({ default: m.NewsScreen })));
const NewsPostScreen = lazy(() => import('./screens/NewsScreen').then((m) => ({ default: m.NewsPostScreen })));
const CommunityBandasScreen = lazy(() => import('./screens/CommunityBandasScreen').then((m) => ({ default: m.CommunityBandasScreen })));
const BandaScreen = lazy(() => import('./screens/BandaScreen').then((m) => ({ default: m.BandaScreen })));
const BandaChatScreen = lazy(() => import('./screens/BandaChatScreen').then((m) => ({ default: m.BandaChatScreen })));
const BandaSettingsScreen = lazy(() => import('./screens/BandaSettingsScreen').then((m) => ({ default: m.BandaSettingsScreen })));
const CommunityChallengesScreen = lazy(() => import('./screens/CommunitySectionScreens').then((m) => ({ default: m.CommunityChallengesScreen })));
const CommunityLeaderboardScreen = lazy(() => import('./screens/CommunitySectionScreens').then((m) => ({ default: m.CommunityLeaderboardScreen })));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));
const PublicProfileScreen = lazy(() => import('./screens/PublicProfileScreen').then((m) => ({ default: m.PublicProfileScreen })));
const ActivityScreen = lazy(() => import('./screens/ActivityScreen').then((m) => ({ default: m.ActivityScreen })));
const SearchScreen = lazy(() => import('./screens/SearchScreen').then((m) => ({ default: m.SearchScreen })));
const MissionsScreen = lazy(() => import('./screens/MissionsScreen').then((m) => ({ default: m.MissionsScreen })));
const ProfileRivalsScreen = lazy(() => import('./screens/ProfileSectionScreens').then((m) => ({ default: m.ProfileRivalsScreen })));
const ProfileStatsScreen = lazy(() => import('./screens/ProfileSectionScreens').then((m) => ({ default: m.ProfileStatsScreen })));
const ProfileBandasScreen = lazy(() => import('./screens/ProfileSectionScreens').then((m) => ({ default: m.ProfileBandasScreen })));
const TerritoryHistoryScreen = lazy(() => import('./screens/TerritoryHistoryScreen').then((m) => ({ default: m.TerritoryHistoryScreen })));
const GpHistoryScreen = lazy(() => import('./screens/GpHistoryScreen').then((m) => ({ default: m.GpHistoryScreen })));
const ProfileBadgesScreen = lazy(() => import('./screens/ProfileSectionScreens').then((m) => ({ default: m.ProfileBadgesScreen })));
const ProfilePassportScreen = lazy(() => import('./screens/ProfileSectionScreens').then((m) => ({ default: m.ProfilePassportScreen })));
const SettingsScreen = lazy(() => import('./screens/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const AppearanceScreen = lazy(() => import('./screens/settings/AppearanceScreen').then((m) => ({ default: m.AppearanceScreen })));
const GraphicsScreen = lazy(() => import('./screens/settings/GraphicsScreen').then((m) => ({ default: m.GraphicsScreen })));
const AppLockScreen = lazy(() => import('./screens/settings/AppLockScreen').then((m) => ({ default: m.AppLockScreen })));
const FinishGestureScreen = lazy(() => import('./screens/settings/FinishGestureScreen').then((m) => ({ default: m.FinishGestureScreen })));
const LocaleScreen = lazy(() => import('./screens/settings/LocaleScreen').then((m) => ({ default: m.LocaleScreen })));
const SoundsScreen = lazy(() => import('./screens/settings/SoundsScreen').then((m) => ({ default: m.SoundsScreen })));
const LocationScreen = lazy(() => import('./screens/settings/LocationScreen').then((m) => ({ default: m.LocationScreen })));
const PrivacyScreen = lazy(() => import('./screens/settings/PrivacyScreen').then((m) => ({ default: m.PrivacyScreen })));
const LiveStreamScreen = lazy(() => import('./screens/settings/LiveStreamScreen').then((m) => ({ default: m.LiveStreamScreen })));
const ConnectedDevicesScreen = lazy(() => import('./screens/settings/ConnectedDevicesScreen').then((m) => ({ default: m.ConnectedDevicesScreen })));
const RulesScreen = lazy(() => import('./screens/settings/RulesScreen').then((m) => ({ default: m.RulesScreen })));
const NotificationsScreen = lazy(() => import('./screens/settings/NotificationsScreen').then((m) => ({ default: m.NotificationsScreen })));
const BlockedUsersScreen = lazy(() => import('./screens/settings/BlockedUsersScreen').then((m) => ({ default: m.BlockedUsersScreen })));
const EditProfileScreen = lazy(() => import('./screens/settings/EditProfileScreen').then((m) => ({ default: m.EditProfileScreen })));
const PrivateAccountScreen = lazy(() => import('./screens/settings/PrivateAccountScreen').then((m) => ({ default: m.PrivateAccountScreen })));
const MessagesScreen = lazy(() => import('./screens/messages/MessagesScreen').then((m) => ({ default: m.MessagesScreen })));
const ConversationScreen = lazy(() => import('./screens/messages/ConversationScreen').then((m) => ({ default: m.ConversationScreen })));
const MessagingSettingsScreen = lazy(() => import('./screens/settings/MessagingSettingsScreen').then((m) => ({ default: m.MessagingSettingsScreen })));
const AccountDeletionScreen = lazy(() => import('./screens/settings/AccountDeletionScreen').then((m) => ({ default: m.AccountDeletionScreen })));
const LinkedAccountsScreen = lazy(() => import('./screens/settings/LinkedAccountsScreen').then((m) => ({ default: m.LinkedAccountsScreen })));
const DataExportScreen = lazy(() => import('./screens/settings/DataExportScreen').then((m) => ({ default: m.DataExportScreen })));
const BodyFitnessScreen = lazy(() => import('./screens/settings/BodyFitnessScreen').then((m) => ({ default: m.BodyFitnessScreen })));
const MutedUsersScreen = lazy(() => import('./screens/settings/BlockedUsersScreen').then((m) => ({ default: m.MutedUsersScreen })));
const SupportScreen = lazy(() => import('./screens/legal/SupportScreen').then((m) => ({ default: m.SupportScreen })));
const PrivacyPolicyScreen = lazy(() => import('./screens/legal/PrivacyPolicyScreen').then((m) => ({ default: m.PrivacyPolicyScreen })));
const TermsScreen = lazy(() => import('./screens/legal/TermsScreen').then((m) => ({ default: m.TermsScreen })));
const AccountDeletionPublicScreen = lazy(() => import('./screens/legal/AccountDeletionPublicScreen').then((m) => ({ default: m.AccountDeletionPublicScreen })));
const WelcomeScreen = lazy(() => import('./screens/auth/WelcomeScreen').then((m) => ({ default: m.WelcomeScreen })));
const LoginScreen = lazy(() => import('./screens/auth/LoginScreen').then((m) => ({ default: m.LoginScreen })));
const PublicActivityScreen = lazy(() => import('./screens/PublicActivityScreen').then((m) => ({ default: m.PublicActivityScreen })));
const RegisterScreen = lazy(() => import('./screens/auth/RegisterScreen').then((m) => ({ default: m.RegisterScreen })));
const ForgotPasswordScreen = lazy(() => import('./screens/auth/ForgotPasswordScreen').then((m) => ({ default: m.ForgotPasswordScreen })));
const CompleteProfileScreen = lazy(() => import('./screens/auth/CompleteProfileScreen').then((m) => ({ default: m.CompleteProfileScreen })));
const FirstFollowsScreen = lazy(() => import('./screens/onboarding/FirstFollowsScreen').then((m) => ({ default: m.FirstFollowsScreen })));
const ProfileWizard = lazy(() => import('./screens/onboarding/ProfileWizard').then((m) => ({ default: m.ProfileWizard })));
const ProfileWizardScreen = lazy(() => import('./screens/onboarding/ProfileWizardScreen').then((m) => ({ default: m.ProfileWizardScreen })));

/**
 * Az admin terület LUSTÁN töltődik.
 *
 * Ez a `docs/06` forma-döntésének a lényege: az admin ugyanabban az
 * alkalmazásban él, de saját JS-darabban — a játékos böngészője egyetlen
 * bájtot sem kér le belőle. Ha valaki ezt statikus importra cseréli, a
 * belépő csomag azonnal megnő, és a döntés indoka elvész.
 */
const AdminArea = lazy(() => import('./admin'));

/**
 * A TESZTELŐI DEBUG RÉTEG — lustán, és csak jogosultnak.
 *
 * Ugyanaz az elv, mint az adminnál: a normál felhasználó böngészője egyetlen
 * bájtot sem tölt le belőle, mert a `lazy` import csak akkor fut le, amikor a
 * komponens tényleg a fába kerül. A jogosultság (`tester` mező vagy admin
 * szerepkör) a `debugModeAvailable()`-ben dől el.
 */
const DebugLayer = lazy(() =>
  import('./components/DebugLayer').then((m) => ({ default: m.DebugLayer })),
);

/**
 * A Game Loop futtató — lustán, ÉS a kapcsoló mögé zárva.
 *
 * ⚠️ A FELTÉTELNEK ITT KELL LENNIE, NEM CSAK A HASZNÁLATNÁL. Először a `lazy`
 * hívás feltétel nélkül állt, a `GAME_LOOP_BUILD` csak az útvonalat őrizte —
 * és MÉRVE (2026-09-05) a kiadási `npm run build` így is legyártotta a
 * `LabGameLoopScreen` chunkot, benne a 29 kB-os mérőpályával. Az útvonal
 * elérhetetlen volt, de a kód kiment. A `define` a `GAME_LOOP_BUILD`-et
 * literál `false`-ra cseréli, így viszont a dinamikus import az ággal együtt
 * kiesik, és a chunk meg sem születik.
 */
const LabGameLoopScreen = GAME_LOOP_BUILD
  ? lazy(() => import('@/admin/LabGameLoopScreen').then((m) => ({ default: m.LabGameLoopScreen })))
  : null;

export function App() {
  return (
    <AuthProvider>
      {/* TODO(F1): a `coords` a helymeghatározásból, a `recordingActive` a
          tracking store-ból jöjjön. Addig a fix idősávos automatika fut. */}
      <ProfileProvider>
        <ThemeProvider coords={null} recordingActive={false}>
          {/* A v6-ban előre bekapcsolt viselkedések React Router v7-ben már
              alapértelmezettek, ezért nincs többé `future` prop. */}
          <BrowserRouter>
            <PlayAnalyticsConsent />
            {/* A lap tetejére ugrás előre, és a hely visszaadása vissza —
                lásd a komponens fejlécét. A Router ALATT kell lennie, mert
                `useLocation`-t használ. */}
            <ScrollManager />
            <NativePushActions />
            {/* Shared GRUNDO links tapped on the phone open here (#105). */}
            <AppLinkActions />
            {/* A rivális-halmaz a router ALATT nem lenne elég: a „RIVÁLIS"
                címke minden képernyőn megjelenhet, tehát egyetlen közös
                betöltésnek kell fölöttük állnia. */}
            <RivalProvider>
              {/* ⚠️ GRUNDO #21, A5: EGYETLEN `onSnapshot` a Kezdőlap harangja
                  ÉS a NotificationPanel között — korábban mindkettő saját
                  feliratkozást nyitott, nyitott panellel egyszerre kettőt. */}
              <NotificationsProvider>
                {/* #68: ugyanaz az elv — egy postafiók-feliratkozás a Kezdőlap
                    üzenetgombjának és az Üzenetek képernyőnek. */}
                <InboxProvider>
                  <RecorderProvider>
                    {/* Új jelvény értesítése → a profil újratöltődik, különben a
                        jelvénylista a kiosztás ELŐTTI állapotból dolgozna — lásd a
                        komponens fejlécét. Az értesítés- ÉS a profil-szolgáltatón
                        BELÜL kell lennie, mert mindkettőt használja. */}
                    <BadgeSync />
                    <Router />
                  </RecorderProvider>
                </InboxProvider>
              </NotificationsProvider>
            </RivalProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

function NativePushActions() {
  const navigate = useNavigate();

  useEffect(() => {
    let dispose: (() => void) | undefined;
    let active = true;
    void addNativePushActionListener(navigate).then((listenerDispose) => {
      if (active) dispose = listenerDispose;
      else listenerDispose();
    });
    return () => {
      active = false;
      dispose?.();
    };
  }, [navigate]);

  return null;
}

function AppUsageHeartbeat({ uid }: { uid: string }) {
  useEffect(() => {
    if (!uid) return;
    return startAppUsageTracking();
  }, [uid]);
  return null;
}

/**
 * Az Android vissza gomb — a döntési sorrend a `useAndroidBack`-ben.
 *
 * Itt csak a „még egyszer a kilépéshez" üzenet jelenik meg. Nem `alert`:
 * az blokkolná a felületet, és a második megnyomás a párbeszédre menne, nem
 * a kilépésre. Ez a sáv magától eltűnik, ha a felhasználó meggondolja magát.
 */
function AndroidBack() {
  useT();
  const { confirmingExit } = useAndroidBack();
  if (!confirmingExit) return null;
  return (
    <div className="exit-hint" role="status">
      {getT().remainingUI.exitHint}
    </div>
  );
}

/** A Grout-chat, a bejelentkezett felhasználóhoz kötve (fiókváltáskor a helyi beszélgetés törlődik). */
function AssistantLayer({ uid }: { uid: string | null }) {
  const bindUser = useAssistantStore((state) => state.bindUser);
  useEffect(() => {
    bindUser(uid);
  }, [bindUser, uid]);
  return <AssistantChat />;
}

function Router() {
  const { status, role, user, signOut } = useAuth();
  /**
   * ⚠️ EGYSZER SZÁMOLJUK KI, a komponens születésekor. A `needsUnlock()`
   * azt nézi, fel van-e oldva EZ A BETÖLTÉS — ha minden renderkor
   * újrakérdeznénk, a feloldás utáni első render már megint zárnának
   * látszana, amíg a modulszintű jelző és a React állapota nem ér össze.
   */
  const [locked, setLocked] = useState(needsUnlock);
  const { status: profileStatus, profile, justCreated, clearJustCreated } = useProfile();
  /* Profile wizard (#96): once for everyone — see the gate below. */
  const onboarding = useOnboarding(profileStatus === 'ready' ? profile?.uid : null);
  const wizardClosed = useOnboardingStore((state) => state.wizardClosed);
  /**
   * A Dock CSAK a mentőlap alatt tűnik el.
   *
   * A mentőlap az, ahol az aktivitás neve, leírása és képei megadhatók — az
   * a feltöltés SIKERE után jelenik meg (`upload.status === 'done'`). Ott a
   * Dock tényleg fölösleges, mert az űrlapé a képernyő.
   *
   * Korábban MINDEN `finished` állapotban eltűnt, és ez két zsákutcát
   * csinált: egy túl rövid rögzítés után az üzenet közölte, hogy nem számít
   * az aktivitás, de nem volt mivel továbbmenni; feltöltési hiba esetén
   * ugyanígy. Egy figyelmeztetésnek nem jár a teljes képernyő.
   *
   * A mentés után az „Új rögzítés" gomb visszaviszi tétlen állapotba, és
   * ezzel a Dock is visszajön.
   *
   * ⚠️ KÜLÖN, KÖNNYŰ CONTEXT (`useRecorderUploadStatus`), NEM
   * `useRecorderContext()` — lásd a `RecorderProvider.tsx` magyarázatát
   * (GRUNDO #21, B5). Ez a komponens a Router GYÖKERE: a teljes
   * `useRecorderContext()` a GPS-pontok tömbjét is hordozza, ami minden
   * mintánál új objektum, tehát a teljes app-fát újrarenderelte volna
   * minden egyes mintánál.
   */
  const uploadStatus = useRecorderUploadStatus();
  const savePanelOpen = uploadStatus === 'done';
  const { pathname } = useLocation();
  /**
   * Jogosult-e a tesztelői debug rétegre. A `tester` mező a szerverről jön,
   * és kizárólag adminból állítható — a szerepkör-claim ugyanígy. Ez itt
   * KÉNYELEM, nem védelem: a beküldő végpont maga is ellenőrzi
   * (`server/src/routes/bugreports.ts` → `assertReporter`).
   */
  const debugAvailable = debugModeAvailable({ tester: profile?.tester, role });
  const localePreviewAvailable = isLocalePreviewAvailable(role);

  /*
   * Public rollout honors device preferences for every account. The preview
   * gate remains available if a future rollout needs to be closed again.
   */
  useLayoutEffect(() => {
    setLocalePreviewEnabled(localePreviewAvailable);
  }, [localePreviewAvailable]);

  /**
   * A GAME LOOP FUTTATÓ — MINDEN KAPU ELŐTT.
   *
   * ⚠️ MÉG A `status === 'loading'` ÁG ELŐTT IS. A Firebase Test Lab
   * készülékén nincs bejelentkezett felhasználó, és hálózat híján a
   * hitelesítés `loading`-ban is ragadhat — akkor a mérőfutás egy Splash
   * képernyőt bámulna végig, majd időtúllépéssel zárna, mérés nélkül.
   *
   * A belépő csak a `GRUNDO_GAMELOOP=1`-gyel készült buildben létezik
   * (`vite.config.ts`), éles csomagból kiesik.
   */
  if (LabGameLoopScreen && pathname === '/gameloop') {
    return (
      <Suspense fallback={<Splash />}>
        <LabGameLoopScreen />
      </Suspense>
    );
  }

  /**
   * AZ ÉLŐ KÖZVETÍTÉS NYILVÁNOS NÉZETE — SZINTÉN MINDEN KAPU ELŐTT.
   *
   * ⚠️ A hitelesítés BEVÁRÁSA ELŐTT is. Ez az oldal idegeneknek szól: egy
   * Twitch-néző, aki a chatben kapott linkre kattint, nincs bejelentkezve,
   * és soha nem is lesz. Ha a `status === 'loading'` kapu mögé kerülne, egy
   * kijelentkezett látogató előbb a Splasht nézné, majd a `!signedIn` ág az
   * üdvözlőlapra dobná — vagyis a link soha nem működne annak, akinek szól.
   *
   * A képernyő semmit nem vesz a providerektől: a `liveStreams/{uid}`
   * dokumentumot olvassa közvetlenül (`src/screens/LiveViewerScreen.tsx`).
   */
  if (pathname.startsWith('/live/')) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          {/* A statikus útvonal előbb: a „battle" nem felhasználónév. */}
          <Route path="/live/battle" element={<LiveBattleScreen />} />
          <Route path="/live/:username" element={<LiveViewerScreen />} />
        </Routes>
      </Suspense>
    );
  }

  /**
   * A JOGI OLDALAK — szintén minden kapu előtt.
   *
   * ⚠️ A hitelesítés bevárása előtt is: a regisztrációs jelölőnégyzet, a
   * store-oldalak és egy megosztott link is elérheti ezt kijelentkezve.
   * Ugyanaz az elv, mint a `/live/`-nál fentebb.
   */
  if (pathname.startsWith('/jogi/')) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/jogi/adatvedelem" element={<PrivacyPolicyScreen />} />
          <Route path="/jogi/aszf" element={<TermsScreen />} />
          <Route path="*" element={<Navigate to="/jogi/adatvedelem" replace />} />
        </Routes>
      </Suspense>
    );
  }

  /**
   * A PUBLIKUS FIÓKTÖRLÉSI OLDAL — szintén minden kapu előtt.
   *
   * ⚠️ A Google Play megköveteli, hogy a törlés belépés nélkül is elérhető
   * URL-ről induljon (`AccountDeletionPublicScreen` fejléce). A képernyő
   * saját maga dönti el `useAuth()`/`useProfile()` alapján, mit mutasson —
   * NEM várhatja meg a `status === 'loading'` kaput, mert épp azt az állapotot
   * kell megkülönböztetnie a kijelentkezettől.
   */
  /** A TÁMOGATÁS — minden kapu előtt: az App Store „Support URL”-je (#80, B4). */
  if (pathname === '/tamogatas') {
    return (
      <Suspense fallback={<Splash />}>
        <SupportScreen />
      </Suspense>
    );
  }

  if (pathname === '/fiok-torles') {
    return (
      <Suspense fallback={<Splash />}>
        <AccountDeletionPublicScreen />
      </Suspense>
    );
  }

  if (status === 'loading') return <Splash />;

  /**
   * Helyi fejlesztésben, Firebase-konfiguráció nélkül átengedjük a
   * felhasználót az appba, hogy a felületek fejleszthetők legyenek.
   * Élesben a hiányzó konfiguráció a bejelentkezésen áll meg.
   */
  const devBypass = import.meta.env.DEV && status === 'unconfigured';
  const signedIn = status === 'signed-in' || devBypass;
  const isAdminRoute = pathname.startsWith('/admin');

  const authRoutes = (
    <>
      <Route path="/udvozles" element={<WelcomeScreen />} />
      <Route path="/belepes" element={<LoginScreen />} />
      <Route path="/regisztracio" element={<RegisterScreen />} />
      <Route path="/elfelejtett-jelszo" element={<ForgotPasswordScreen />} />
    </>
  );

  if (!signedIn) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/admin/*" element={<AdminArea />} />
          {authRoutes}
          {/* A shared activity link opened while signed out (#105): the public view, not the welcome screen. */}
          <Route path="/aktivitas/:id" element={<PublicActivityScreen />} />
          <Route path="*" element={<Navigate to="/udvozles" replace />} />
        </Routes>
      </Suspense>
    );
  }

  /**
   * A BIOMETRIKUS ZÁR — a bejelentkezés UTÁN, minden más elé.
   *
   * ⚠️ A SORREND SZÁNDÉKOS. A zár csak bejelentkezett felhasználót véd: aki
   * nincs belépve, annak nincs mit elzárni, és a belépőt elzárni értelmetlen
   * lenne. A profil betöltése viszont már MÖGÖTTE van, mert az a felhasználó
   * adata — azt is csak feloldás után kérjük le.
   */
  if (locked && !isAdminRoute) {
    return (
      <AppLockGate
        onUnlocked={() => setLocked(false)}
        onSignOut={() => {
          setLocked(false);
          void signOut();
        }}
      />
    );
  }

  // Van Firebase-fiók, de nincs GRUNDO-profil → előbb felhasználónév kell.
  // A `unavailable` (nincs backend) NEM állítja meg az appot.
  if (!isAdminRoute && profileStatus === 'missing') return <CompleteProfileScreen />;
  if (!isAdminRoute && profileStatus === 'loading') return <Splash />;
  /**
   * A profil betöltésének hibája NEM engedhető át némán.
   *
   * Korábban itt nem volt ág, így a hiba egyszerűen a Home-ra vitt: a
   * felhasználó profil nélkül, de látszólag rendben belépett, és semmi nem
   * jelezte, hogy baj van. Egy elrontott szerveroldali útvonal-bekötés így
   * napokig észrevétlen maradhat. Inkább álljunk meg és mondjuk meg, mi a baj.
   */
  if (!isAdminRoute && profileStatus === 'error') return <ProfileError />;

  /**
   * PROFILE WIZARD (#96) — ONCE for everyone: a new user right after sign-up,
   * BEFORE „Első követések"; existing users on the first start after the
   * release. The mark is `private/settings.onboarding.profileWizardAt`.
   *
   * ⚠️ No Splash while waiting: until the settings document answers (offline
   * it may never), the app goes on — a one-off wizard must not hold up the
   * start. Not shown for an account pending deletion.
   */
  if (
    !isAdminRoute &&
    onboarding.loaded &&
    onboarding.profileWizardAt === null &&
    !wizardClosed &&
    !profile?.deletion &&
    pathname !== '/beallitasok/profilvarazslo'
  ) {
    return (
      <Suspense fallback={<Splash />}>
        <AndroidBack />
        <ProfileWizard onClose={() => {}} />
      </Suspense>
    );
  }

  /**
   * ONBOARDING 7. LÉPÉS — „Első követések", CSAK EBBEN A MUNKAMENETBEN
   * frissen létrejött fióknál (`ProfileProvider.justCreated`, `docs/02` →
   * Onboarding 7.). Szándékosan a türelmi idő gate ELŐTT — logikailag ez a
   * regisztráció folytatása, egy vadonatúj fióknak pedig sosincs törlése.
   */
  if (justCreated && !isAdminRoute) {
    return <FirstFollowsScreen onDone={clearJustCreated} />;
  }

  /**
   * A TÜRELMI IDŐ ALATTI BELÉPÉS — a normál app elé áll.
   *
   * ⚠️ MINDEN MÁS ELŐTT, a Dock és a képernyők előtt: a szerver a
   * `deletionPending` claimmel úgyis 403-ra utasítana mindent az
   * engedélylistán kívül (`accountDeletion.ts` → `ALLOWED_DURING_DELETION`),
   * tehát a felület se kínáljon fel olyat, ami úgysem menne végig.
   */
  if (profile?.deletion) {
    return <DeletionPendingGate deletion={profile.deletion} />;
  }

  if (isAdminRoute) {
    return (
      <div className="app-shell app-shell--wide">
        <Suspense fallback={<Splash />}>
          <Routes>
            <Route path="/admin/*" element={<AdminArea />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <>
      <AppUsageHeartbeat uid={user?.uid ?? ''} />
      {/* „Még nem hitelesítetted az e-mail-címedet” — bármely `community_locked` hibára (#76). */}
      <CommunityLockPrompt />
      {/* „Grout” chat (#79) — App szinten, hogy a Home → rögzítés átlépést túlélje. */}
      <AssistantLayer uid={user?.uid ?? null} />
      {/* Bemutató (#96): a „Megnézed?” kérdés és a coach-markok, a Home-on. */}
      <TutorialLayer uid={user?.uid ?? null} onboarding={onboarding} />
      {/* Az Android vissza gomb — a viselkedése a hookban, egy helyen. */}
      <AndroidBack />
      {/*
        AZ ADMIN TELJES SZÉLESSÉGŰ — Geri kérése (2026-09-01).

        A játékos-felület asztali gépen is mobil szélességű oszlop
        (`--app-max-width`, 480 px), és ez szándékos. Az admin viszont sűrű,
        táblázatos, asztali gépre szánt felület (lásd `admin.css` fejléce) —
        480 pixelbe préselve a Simulation LAB térképe és az aktivitás-audit
        táblái használhatatlanok voltak.

        A rögzítés képernyője (LAB E2E is) ez alól kivétel MARAD: a `.track`
        gyökere `position: fixed`, a felülete pedig saját `max-width`-et
        használ, tehát ott továbbra is a telefonos elrendezés látszik — pont
        ez kell ahhoz, hogy a LAB azt mutassa, amit a felhasználó lát.
      */}
      <div className={`app-shell${pathname.startsWith('/admin') ? ' app-shell--wide' : ''}`}>
        <Suspense fallback={<Splash />}>
          <Routes>
            {/* Fejlesztői átengedés esetén a belépési képernyők is elérhetők
                maradnak, különben nem lehetne rajtuk dolgozni. */}
            {devBypass ? authRoutes : null}
            <Route path="/" element={<HomeScreen />} />
            <Route path="/grund" element={<TerritoryScreen />} />
            {/* A régi cím megmarad átirányításként: könyvjelzők és megosztott
                hivatkozások ne törjenek el egy átnevezés miatt. */}
            <Route path="/terulet" element={<Navigate to="/grund" replace />} />
            <Route path="/rogzites" element={<TrackingScreen />} />
            {/* News is the first Community tab (#130); Explore moved under /felfedezes. */}
            <Route path="/kozosseg" element={<NewsScreen />} />
            <Route path="/kozosseg/hirek/:id" element={<NewsPostScreen />} />
            <Route path="/kozosseg/felfedezes" element={<DiscoverScreen />} />
            <Route path="/kozosseg/bandak" element={<CommunityBandasScreen />} />
            <Route path="/bandak/:id" element={<BandaScreen />} />
            <Route path="/bandak/:id/beallitasok" element={<BandaSettingsScreen />} />
            <Route path="/bandak/:id/chat" element={<BandaChatScreen />} />
            <Route path="/kozosseg/kihivasok" element={<CommunityChallengesScreen />} />
            <Route path="/kozosseg/toplista" element={<CommunityLeaderboardScreen />} />
            {/* The passport moved to the profile (2026-10-15); old links keep working. */}
            <Route path="/kozosseg/utlevel" element={<Navigate to="/profil/utlevel" replace />} />
            <Route path="/profil" element={<ProfileScreen />} />
            <Route path="/profil/rivalisok" element={<ProfileRivalsScreen />} />
            <Route path="/profil/statisztikak" element={<ProfileStatsScreen />} />
            <Route path="/profil/terulet-tortenet" element={<TerritoryHistoryScreen />} />
            <Route path="/profil/gp-tortenet" element={<GpHistoryScreen />} />
            <Route path="/profil/bandak" element={<ProfileBandasScreen />} />
            <Route path="/profil/badgek" element={<ProfileBadgesScreen />} />
            <Route path="/profil/utlevel" element={<ProfilePassportScreen />} />
            <Route path="/profil/badges" element={<Navigate to="/profil/badgek" replace />} />
            {/* Más felhasználó profilja. A `/profil` a sajátom, ez bárki másé. */}
            <Route path="/felhasznalo/:username" element={<PublicProfileScreen />} />
            <Route path="/kereses" element={<SearchScreen />} />
            <Route path="/uzenetek" element={<MessagesScreen />} />
            <Route path="/uzenetek/:conversationId" element={<ConversationScreen />} />
            <Route path="/kuldetesek" element={<MissionsScreen />} />
            <Route path="/aktivitas/:id" element={<ActivityScreen />} />
            <Route path="/beallitasok" element={<SettingsScreen />} />
            <Route path="/beallitasok/megjelenes" element={<AppearanceScreen />} />
            <Route path="/beallitasok/grafika" element={<GraphicsScreen />} />
            <Route path="/beallitasok/zar" element={<AppLockScreen />} />
            <Route path="/beallitasok/hangok" element={<SoundsScreen />} />
            <Route path="/beallitasok/lokacio" element={<LocationScreen />} />
            <Route path="/beallitasok/mukodes" element={<FinishGestureScreen />} />
            <Route path="/beallitasok/nyelv" element={<LocaleScreen />} />
            <Route path="/beallitasok/profilvarazslo" element={<ProfileWizardScreen />} />
            <Route path="/beallitasok/adatvedelem" element={<PrivacyScreen />} />
            <Route path="/beallitasok/elo-kozvetites" element={<LiveStreamScreen />} />
            <Route path="/beallitasok/osszekapcsolt-eszkozok" element={<ConnectedDevicesScreen />} />
            <Route path="/beallitasok/szabalyok" element={<RulesScreen />} />
            <Route path="/beallitasok/ertesitesek" element={<NotificationsScreen />} />
            <Route path="/beallitasok/tiltottak" element={<BlockedUsersScreen />} />
            <Route path="/beallitasok/nemitottak" element={<MutedUsersScreen />} />
            <Route path="/beallitasok/profil" element={<EditProfileScreen />} />
            <Route path="/beallitasok/privat-fiok" element={<PrivateAccountScreen />} />
            <Route path="/beallitasok/uzenetek" element={<MessagingSettingsScreen />} />
            <Route path="/beallitasok/fiok-torlese" element={<AccountDeletionScreen />} />
            <Route path="/beallitasok/bejelentkezesi-modok" element={<LinkedAccountsScreen />} />
            <Route path="/beallitasok/adatexport" element={<DataExportScreen />} />
            <Route path="/beallitasok/test-fitnesz" element={<BodyFitnessScreen />} />
            {/* A régi fejlesztői címek megmaradnak átirányításként: a
                könyvjelzők és a dokumentációban szereplő hivatkozások ne
                törjenek el a beköltöztetés miatt. */}
            <Route path="/dev/replay" element={<Navigate to="/admin/aktivitasok" replace />} />
            <Route path="/dev/activities" element={<Navigate to="/admin/aktivitasok" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {/* Az aktivitás adatlapja saját, teljes képernyős navigációt kap. A
          rögzítő ettől továbbra is app-szinten él; csak a Dock nem takarja el
          az adatlap alsó tartalmát. A Beállítások is ugyanígy: hosszú,
          görgethető listás oldalak, ahol a Dock csak eltakarta az utolsó
          sorokat — Geri kérése (2026-08-27), lásd a „Bejelentkezve" sort. */}
      {pathname.startsWith('/aktivitas/') ||
      pathname.startsWith('/admin') ||
      pathname.startsWith('/beallitasok') ||
      /* #68: a szál alján a szövegmező van — a Dock eltakarná. */
      pathname.startsWith('/uzenetek/') ||
      /^\/bandak\/[^/]+\/beallitasok\/?$/.test(pathname) ||
      /* #71: a banda chat alján is szövegmező van. */
      /^\/bandak\/[^/]+\/chat\/?$/.test(pathname) ||
      (pathname === '/rogzites' && savePanelOpen) ? null : <Dock />}
      {/*
        A FÉLBEHAGYOTT AKTIVITÁS megerősítése — bármelyik képernyőn, azonnal
        app-indítás után. Lásd `ResumeActivityPrompt.tsx` fejlécét: a
        Rögzítés képernyőbe zárt banner nem elég, mert a felhasználó nem
        biztos, hogy oda navigál (GRUNDO #42).
      */}
      <ResumeActivityPrompt />
      {/* New country in the passport → the stamping moment (DECISIONS #101). */}
      <PassportStampHost />
      {/*
        A tesztelői debug réteg — a Dock és a felugró lapok FÖLÖTT, mert pont
        azt kell tudni bejelenteni, ami eltakarja a felületet. A `Suspense`
        `null` visszajelzéssel: a betöltése nem villanthat Splash képernyőt a
        működő app fölé.
      */}
      {debugAvailable ? (
        <Suspense fallback={null}>
          <DebugLayer recorder={uploadStatus} uid={user?.uid ?? ''} />
        </Suspense>
      ) : null}
    </>
  );
}

function ProfileError() {
  const { error, reload } = useProfile();
  const { signOut } = useAuth();
  const t = useT();

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'var(--bg-primary)',
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center', display: 'grid', gap: 16 }}>
        <span style={{ fontSize: 40 }} aria-hidden="true">
          ⚠️
        </span>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
          {t.profileError.title}
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          {error || t.profileError.unknown}
        </p>
        <Button block onClick={() => void reload()}>
          {t.profileError.retry}
        </Button>
        <Button variant="ghost" block onClick={() => void signOut()}>
          {t.common.signOut}
        </Button>
      </div>
    </main>
  );
}

/**
 * „VISSZAVONOD A TÖRLÉST?" — a türelmi idő alatti belépéskor, mielőtt bármi
 * más elérhető lenne (terv 1.1 H, GRUNDO #64; `docs/05` → Fióktörlés
 * folyamata). Ha a felhasználó belépett, de korábban törlést kért, ez a
 * gate áll az app elé — nincs Dock, nincs navigáció, csak ez a döntés.
 */
function DeletionPendingGate({ deletion }: { deletion: AccountDeletionState }) {
  const { user, signOut } = useAuth();
  const { patchProfile } = useProfile();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function cancel() {
    setBusy(true);
    setError('');
    try {
      await api.cancelAccountDeletion();
      // A jelenlegi token még a törlési claimet hordozza — friss kell,
      // különben a következő hívás 403 `account_deletion_pending`-gel elhasal.
      await user?.getIdToken(true);
      patchProfile({ deletion: null });
    } catch (problem: unknown) {
      setError(problem instanceof ApiError ? problem.message : t.deletionPending.cancelFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'var(--bg-primary)',
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center', display: 'grid', gap: 16 }}>
        <span style={{ fontSize: 40 }} aria-hidden="true">
          ⏳
        </span>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
          {t.deletionPending.title}
        </h1>
        {deletion.status === 'pending' ? (
          <>
            <p style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              {t.deletionPending.pendingBeforeDate}
              <strong>{formatDateTime(deletion.scheduledFor)}</strong>
              {t.deletionPending.pendingAfterDate}
            </p>
            {error ? (
              <p role="alert" style={{ color: 'var(--danger, #e5484d)', fontSize: 14 }}>
                {error}
              </p>
            ) : null}
            <Button block loading={busy} onClick={() => void cancel()}>
              {t.deletionPending.cancel}
            </Button>
          </>
        ) : (
          <p style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            {t.deletionPending.running}
          </p>
        )}
        <Button variant="ghost" block onClick={() => void signOut()}>
          {t.common.signOut}
        </Button>
      </div>
    </main>
  );
}

function Splash() {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg-primary)',
      }}
    >
      {/*
        A logó a szöveges „GRUNDO" felirat helyén — ugyanaz a két PNG, mint a
        Home fejlécében (`HomeScreen.tsx`), a `.splash__logo` szabályok pedig
        a `global.css`-ben élnek, mert ez a komponens azelőtt is renderelhet,
        hogy bármelyik képernyő saját CSS-e betöltődne.
      */}
      {/* Az `alt` csak a világos változaton van kitöltve — ugyanaz a minta,
          mint a Home fejlécében: kétszer felolvasva „GRUNDO GRUNDO" hangzana. */}
      <img className="splash__logo splash__logo--light" src="/grundo-logo-light.png" alt="GRUNDO" />
      <img
        className="splash__logo splash__logo--dark"
        src="/grundo-logo-dark.png"
        alt=""
        aria-hidden="true"
      />
    </main>
  );
}
